/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  bookingPricing,
  BookingConfig,
  DropdownOption,
  FormFieldState,
  OrBookingPanel,
  Timeslot,
  defaultInquireState,
  deskForm,
  inquireForm,
  meetingForm,
  officeForm,
  useFormState,
  OrSecondaryNavbarPdp,
  FeeProps,
  simpleBlock,
  formatCurrency,
  makeDefaultOfficeState,
  makeDefaultDeskState,
  TimeslotList,
} from '@studio/atomic-ui'
import { isSameDay } from 'date-fns'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import type { DateRange } from 'react-day-picker'
import {
  buildBookingQuery,
  deskFormConfig,
  meetingFormConfig,
  checkDayPassAvailability,
  checkSpaceAvailability,
  officeFormConfig,
  pricePlaceholder,
  productTypeNames,
  prepareDateForBackend,
} from './booking-panel-config'
import { setDeskState, setMeetingState, setOfficeState } from './booking-panel-state'
import { TmPdpProps } from './index'
import { DEFAULT_SPACE_NAME, priceLabel } from '../../utils/spaces'
import { LeadRequest, createLead } from '../../services/bff/leads'
import { useQuery, useQueryClient } from 'react-query'
import { transformTime } from '../../utils/get-hours'
import { removePhoneMask } from '../../utils/fields'
import {
  getUtmFields,
  trackEmailSubscribeForm,
  trackForm,
  trackFormStart,
  trackNavigationMenuClick,
  trackPricingInquiryForm,
} from '../../utils/analytics'
import { googleAnalyticsClientId, hubspotEmailErrorMessage, pricingFormStartedSessionItem } from '../../env'
import { useBuildings } from '../../components/buildings-provider'
import analyticsService from '../../analytics/analytics.instance'
import { getGACookie } from '../../utils/google-analytics-cookie'
import { addAnalyticsFieldToForm } from '../../utils/google-analytics-form-field'
import { INIT_CHECKOUT_SESSION_KEY } from '../tm-checkout/constants'
import { customErrorSnackbarMessage } from '../../utils/text'
import { useQueryParams } from '../../hooks/useQueryParams'

export const BookingPanelForm = (props: TmPdpProps) => {
  const queryClient = useQueryClient()
  const { getBuildingByKey } = useBuildings()
  const { query } = useQueryParams()
  const router = useRouter()
  const isMeetingRoom = ['meeting_room', 'event_space'].includes(props.spaceType)
  const { from, to, quantity, duration, labelDuration, startTime } = query as Record<string, string | undefined>
  const [pricing, setPricing] = useState(pricePlaceholder)
  const [multiDay, setMultiDay] = useState(false)
  const [isNotification, setIsNotification] = useState(false)
  const [message, setMessage] = useState(props.inquireMessageSuccess)
  const [isCheckCleared, setIsCheckCleared] = useState(false)
  const [filteredTimeSlots, setFilteredTimeSlots] = useState<TimeslotList[] | undefined>(undefined)

  // Check if 'to' date selected is not beyond product bookable date
  const currentDate = new Date()
  const getBookableDateLimit = (toDate: Date | undefined, bookableDays?: number) => {
    if (toDate !== undefined && bookableDays) {
      toDate.setDate(toDate.getDate() + bookableDays)
      return toDate?.toISOString().split('T')[0]
    }
    return toDate
  }
  const dateLimit = getBookableDateLimit(currentDate, props?.bookableDays)
  const isBeyondDateLimit = dateLimit && to && to.split('T')[0] > dateLimit

  const inquiryOnly =
    props.inquiryOnly || !props.bookableDays || !props.prices || query.inquire === 'true' || isBeyondDateLimit

  const inquiryFormSelection = (): [BookingConfig, FormFieldState] => {
    const defaultInquiryForm = inquireForm()
    if (props.prices?.source === 'sanity' && props.prices.unitPrice) {
      return [
        {
          ...defaultInquiryForm,
          details: {
            ...defaultInquiryForm.details,
            rate: [
              props.textBeforeInquiryPrice,
              `${formatCurrency(props.prices.unitPrice, false, { currency: props.inquiryCurrency || 'USD' })}/${
                props.inquiryRateType ?? 'month'
              }`,
            ]
              .filter(Boolean)
              .join(' '),
            rateSubtitle: props.textAfterInquiryPrice,
          },
        },
        defaultInquireState,
      ]
    } else if (props.inquiryOnly && props.inquiryPrice) {
      return [
        {
          ...defaultInquiryForm,
          details: {
            ...defaultInquiryForm.details,
            rate: [
              props.textBeforeInquiryPrice,
              `${formatCurrency(props.inquiryPrice, false, { currency: props.inquiryCurrency || 'USD' })}/${
                props.inquiryRateType ?? 'month'
              }`,
            ]
              .filter(Boolean)
              .join(' '),
            rateSubtitle: props.textAfterInquiryPrice,
          },
        },
        defaultInquireState,
      ]
    }

    return [defaultInquiryForm, defaultInquireState]
  }

  const formSelection = (): [BookingConfig, FormFieldState] => {
    if (inquiryOnly) return inquiryFormSelection()
    const sameDay = isSameDay(new Date(from as string), new Date(to as string))
    switch (props.spaceType) {
      case 'office': {
        const officeFormParams = { ...officeFormConfig(props), ...pricing }

        if (from && to) {
          return [officeForm(officeFormParams), setOfficeState(from, to, sameDay)]
        } else {
          return [officeForm(officeFormParams), makeDefaultOfficeState(props.holidays)]
        }
      }

      case 'day_pass': {
        const deskFormParams = { ...deskFormConfig(props), ...pricing, holidays: props.holidays }

        if (from && to && quantity) {
          return [deskForm(deskFormParams), setDeskState(from, to, quantity, sameDay)]
        } else {
          return [deskForm(deskFormParams), makeDefaultDeskState(props.holidays)]
        }
      }

      case 'meeting_room':
      case 'event_space': {
        return [
          meetingForm({ ...meetingFormConfig(props), ...pricing, multiDay }),
          setMeetingState(
            props.source,
            from,
            to,
            duration ?? props.minimumBookingDuration?.value,
            labelDuration ?? props.minimumBookingDuration?.title,
            startTime,
            sameDay,
            props.holidays
          ),
        ]
      }

      default:
        return inquiryFormSelection()
    }
  }

  const [form, defaultState] = formSelection()
  const [buttonState, setButtonState] = useState({
    primaryLabel: form.primaryLabel,
    primaryDisabled: false,
  })
  const [state, handler] = useFormState(defaultState)
  const isQueryingEnabled = !state.date?.open && !inquiryOnly
  /** Returns the number of available desks for the current date selection  */
  const desks = useQuery(
    ['desks', state.date?.value],
    () =>
      checkDayPassAvailability(
        props.building || 'the-spiral',
        props.source,
        state.date?.value as DateRange | undefined
      ),
    { enabled: isQueryingEnabled && props.spaceType === 'day_pass' }
  )

  /** Checks for availability based on parameters available in types other than desks, which uses its own query */
  const availability = useQuery(
    ['availability', state.date?.value, state.duration?.value, multiDay],
    () =>
      checkSpaceAvailability(
        props.id,
        props.source,
        !multiDay,
        props.spaceType,
        state.date?.value as DateRange | undefined,
        state.startTime?.value as Timeslot | undefined,
        state.duration?.value as DropdownOption | undefined,
        props.building
      ),
    { enabled: isQueryingEnabled && props.spaceType !== 'day_pass' }
  )

  const fetching =
    (props.spaceType === 'day_pass' && desks.isLoading) || (props.spaceType !== 'day_pass' && availability.isLoading)

  const deskBookingError =
    props.spaceType === 'day_pass' &&
    !desks.isLoading &&
    (!desks.data || desks.data < parseInt((state.desks?.value as DropdownOption).value))

  const bookingError = () => {
    const base = props.spaceType !== 'day_pass' && !availability.isLoading

    const { from, to } = (state.date?.value as DateRange | undefined) ?? {}
    const { timeslot } = (state.startTime?.value as Timeslot | undefined) ?? {}
    const isValidTimeslot =
      availability.data?.isAvailable && availability.data?.startTimes
        ? availability.data.startTimes.find((group) => group.timeslots.find((slot) => slot === timeslot))
        : false
    const multipleDays = from && to && !availability.data?.isAvailable
    const singleDay = from && !to && !!timeslot && !isValidTimeslot
    const singleDayOffice = from && !to && !timeslot && !availability.data?.isAvailable
    return base && (multipleDays || singleDay || singleDayOffice)
  }
  const error = deskBookingError || bookingError()

  const viewItemSentRef = useRef(false)

  useEffect(() => {
    setMultiDay(!!state.date?.value && !!(state.date.value as DateRange)?.to)
    let newPricing = pricing
    if (props.prices?.unitPrice) {
      newPricing = bookingPricing(
        props.prices?.unitPrice,
        props.prices.availableDiscounts,
        state,
        props.spaceType,
        props.holidays
      )
      setPricing(newPricing)
    }

    if (viewItemSentRef.current) {
      return
    }

    viewItemSentRef.current = true

    analyticsService?.trackViewItem({
      coupon: newPricing.reservation.discountCode ?? '',
      platformId: props.id,
      currencyCode: props.prices?.currency ?? 'usd',
      price: newPricing.reservation.rawDiscountedFee ?? 0,
      discount: (newPricing.reservation.rawFee ?? 0) - (newPricing.reservation.rawDiscountedFee ?? 0),
      name: props.description.title,
      location: props.building,
      type: props.spaceType,
      bookingType: props.inquiryOnly ? 'inquiry only' : 'instant book',
      capacity: props.capacity,
      hourlyRate: props.prices?.unitPrice ?? 0,
      source: props.source,
    })
  }, [state])

  useEffect(() => {
    const updateGoogleAnalyticsClientId = () => {
      handler({
        [`${googleAnalyticsClientId}`]: { value: getGACookie('_ga') as string },
      })
    }
    updateGoogleAnalyticsClientId()
  }, [])

  const filterTimeslots = (timeslots: string[], selectedDate: Date, minimumDate: Date) => {
    return timeslots.filter((slot) => {
      const [hour, minute] = slot.split(':')
      const slotDate = new Date(selectedDate)
      slotDate.setHours(parseInt(hour), parseInt(minute), 0, 0)

      return slotDate >= minimumDate
    })
  }

  useEffect(() => {
    if (
      !props.earliestBookableHours ||
      props.earliestBookableHours === 0 ||
      !availability.data?.startTimes ||
      !(state.date.value as any)?.from
    ) {
      setFilteredTimeSlots(availability.data?.startTimes)
      return
    }

    const selectedDate = new Date((state.date.value as any)?.from)
    const minimumDate = new Date()
    minimumDate.setHours(minimumDate.getHours() + props.earliestBookableHours, minimumDate.getMinutes(), 0, 0)

    const filteredTimeslots = availability.data?.startTimes.map((block) => {
      const filteredSlots = filterTimeslots(block.timeslots, selectedDate, minimumDate)
      return {
        label: block.label,
        timeslots: filteredSlots,
        timeslotLabels: block.timeslotLabels?.slice(block.timeslots.length - filteredSlots.length),
        noSlotsError: block.noSlotsError,
      }
    })

    setFilteredTimeSlots(filteredTimeslots)
  }, [state, availability.data?.startTimes])

  const onSubmit = async (valid: boolean, errors?: string[]) => {
    if (!valid) return
    if (inquiryOnly) return onInquire(valid, errors)

    if (!props.id) {
      setMessage(customErrorSnackbarMessage('Something went wrong, please try again later.'))
      setIsNotification(true)
      return
    }

    setButtonState({ ...buttonState, primaryLabel: '', primaryDisabled: true })

    const query = buildBookingQuery(props.id, props.source, state)

    if (await isBookingAvailable()) {
      /*
        Initialize Booking Flow
        ===========================================================
        Only bookings that have been initialized should remain valid
        This can be replaced in the future to pull the booking UUID
        from the bff once generated
      */
      const uuid = crypto.randomUUID()
      sessionStorage.setItem(INIT_CHECKOUT_SESSION_KEY, uuid)

      const { from, to } = prepareDateForBackend(
        state.date.value as DateRange,
        state.startTime?.value as Timeslot | undefined,
        state.duration?.value as DropdownOption | undefined
      )
      analyticsService?.trackAddToCart({
        coupon: pricing.reservation.discountCode ?? '',
        platformId: props.id,
        currencyCode: props.prices?.currency ?? 'usd',
        price: pricing.reservation.rawDiscountedFee ?? pricing.reservation.rawFee ?? 0,
        discount: pricing.reservation.rawDiscountedFee
          ? (pricing.reservation.rawFee ?? 0) - pricing.reservation.rawDiscountedFee
          : 0,
        name: props.description.title,
        location: props.building,
        type: props.spaceType,
        bookingType: props.inquiryOnly ? 'inquiry only' : 'instant book',
        capacity: props.capacity,
        hourlyRate: props.prices?.unitPrice ?? 0,
        source: props.source,
        dateRange: { from: new Date(from), to: new Date(to) },
      })

      const params = new URLSearchParams(query)

      router.push(`/checkout?${params.toString()}`)
    } else {
      await queryClient.invalidateQueries([props.spaceType === 'day_pass' ? 'desks' : 'availability'])
      if (props.spaceType === 'day_pass') revalidateDeskField()
      if (props.spaceType !== 'day_pass') revalidateDateFields()
      setButtonState({ ...buttonState, primaryLabel: form.primaryLabel, primaryDisabled: false })
    }
  }

  const isBookingAvailable = async () => {
    if (props.spaceType === 'day_pass') {
      const deskRevalidation = await desks.refetch()
      return deskRevalidation.data && parseInt((state.desks.value as DropdownOption).value) <= deskRevalidation.data
    }

    const spaceRevalidation = await availability.refetch()
    return spaceRevalidation.data?.isAvailable
  }

  const onInquire = async (valid?: boolean, errors?: string[]) => {
    // Validación igual que Contact Us
    if (valid === false) {
      return
    }

    if (inquiryOnly) {
      trackForm({
        name: form.primaryLabel as string,
        type: 'pricing inquiry' as string,
        id: 'property_pricing_inquiry_form',
        newsletter: state.marketing?.value as boolean,
      })

      trackPricingInquiryForm({
        product_name: `${props.building} | ${props.description.title} | ${props.spaceType}`,
        product_id: props.id?.toString() || 'not available',
        product_type: props.spaceType,
        product_capacity: props.capacity?.toString() || 'none available',
        product_source: props.source,
        building: props.building,
      })

      if (state?.marketing?.value) {
        trackEmailSubscribeForm(form.primaryLabel as string, 'pricing inquiry', 'property_pricing_inquiry_form')
      }
    }

    if (!inquiryOnly) {
      if (!props.id) {
        setMessage(customErrorSnackbarMessage('Something went wrong, please try again later.'))
        setIsNotification(true)
        return
      }

      const query = buildBookingQuery(props.id, props.source, state)
      const building = props.building || DEFAULT_SPACE_NAME

      const params = new URLSearchParams({
        building: building,
        submissionType: 'Pricing',
        type: props.spaceType,
        id: props.id.toString(),
        name: props.description.title,
        capacity: props.capacity ? props.capacity?.toString() : '1',
        from: query.from ?? '',
        to: query.to ?? '',
        bookingSource: props.source ?? '',
        fromPDP: 'true',
      })

      router.push(`/contact?${params.toString()}`)

      return
    }

    setButtonState({ ...buttonState, primaryDisabled: true })
    const body: LeadRequest = {
      email: state.email?.value as string,
      firstName: state.firstName?.value as string,
      lastName: state.lastName?.value as string,
      phoneNumber: removePhoneMask(state.phone?.value as string),
      additionalInfo: state.description?.value ? (state.description?.value as string) : undefined,
      marketingConsent: state.marketing?.value as boolean,
      locationName: getBuildingByKey(props.building)?.name,
      submissionType: 'Pricing', // Inquiries for inquiry only products should always be pricing
      inquiryProductType: productTypeNames[props.spaceType],
      productName: props.description.title,
      productCapacity: props?.capacity ? props?.capacity : 1,
      startDate: query.from as string,
      endDate: query.to as string,
      bookingSource: props.source,
      ...getUtmFields(query),
      googleAnalyticsClientId: state[`${googleAnalyticsClientId}`]?.value as string,
    }

    let response
    try {
      response = await createLead(body)
    } catch {
      setIsNotification(true)
      setButtonState({ ...buttonState, primaryDisabled: false })
      return setMessage(props.failureMessage)
    }

    if ('statusCode' in response && response.statusCode !== 201) {
      if (response.message?.includes(hubspotEmailErrorMessage.replace('{email}', body.email))) {
        setMessage(customErrorSnackbarMessage(response.message.replace(/[[\]"\\]/g, '')))
      } else setMessage(props.failureMessage)
    } else {
      // Only clear the form when submission is successful
      if (state.marketing.value === true) {
        setIsCheckCleared(true)
      }
      handler({
        description: { value: '' },
        email: { value: '' },
        firstName: { value: '' },
        lastName: { value: '' },
        marketing: { value: false },
        phone: { value: '' },
      })
      setMessage(props.inquireMessageSuccess)
    }
    setButtonState({ ...buttonState, primaryDisabled: false })
    setIsNotification(true)
  }

  const checkBookingState = () => {
    const { from, to } = (state.date?.value as DateRange | undefined) ?? {}
    const startTime = (state.startTime?.value as Timeslot | undefined)?.timeslot
    const duration = (state.duration?.value as DropdownOption | undefined)?.value

    if ((from && to) || (from && startTime && duration)) {
      // Book now, the form is complete
      onSubmit(true)
      return
    }

    if (from && duration && !startTime) {
      // Select a start time
      const timeslotSelector = document.getElementById('timeslot-selector')
      timeslotSelector?.scrollIntoView()
      return
    }

    // Date unselected, select a date
    const dateField = document.querySelector<HTMLButtonElement>('button[name="date"]')
    dateField?.focus()
  }

  const navBarTimeCheck = () => {
    if (props.spaceType === 'meeting_room' || props.spaceType === 'event_space') {
      const timeslot = (state.startTime?.value as Timeslot | undefined)?.timeslot
      const duration = (state.duration?.value as DropdownOption)?.label

      if (timeslot && duration) return `${transformTime(timeslot)} (${duration})`
      return
    }
    return ' '
  }

  const revalidateDeskField = () => {
    if (props.spaceType === 'day_pass' && !inquiryOnly) {
      handler({ desks: { ...state['desks'], error: deskBookingError && !fetching } })
    }
  }
  useEffect(revalidateDeskField, [desks.data, desks.isLoading, deskBookingError, fetching])

  const revalidateDateFields = () => {
    if (props.spaceType !== 'day_pass' && !inquiryOnly && state.date.value) {
      const { from, to } = state.date.value as DateRange
      if (from && to) {
        handler({
          date: {
            ...state['date'],
            error: error && !fetching,
          },
        })
      }
      if (from) {
        if (props.spaceType !== 'office') {
          handler({
            startTime: {
              ...state['startTime'],
              loading: availability.isLoading,
              error: error && !fetching,
            },
          })
        } else {
          handler({
            date: {
              ...state['date'],
              error: error && !fetching,
            },
          })
        }
      }
    }
  }
  useEffect(revalidateDateFields, [availability.isLoading, error, fetching])

  const onChange = (value: FormFieldState) => {
    if (!sessionStorage.getItem(pricingFormStartedSessionItem) && inquiryOnly) {
      trackFormStart(form.details.rate as string, 'pricing inquiry', 'property_pricing_inquiry_form')
      sessionStorage.setItem(pricingFormStartedSessionItem, 'true')
    }

    if (isMeetingRoom && (value.duration || value.date)) {
      handler({ ...value, startTime: { value: undefined } })
      return
    }

    handler(value)
  }

  return (
    <>
      <OrBookingPanel
        {...form}
        isInquiryOnly={!!inquiryOnly}
        fields={addAnalyticsFieldToForm(form.fields).map((field) => ({
          ...field,
          ...(props.spaceType === 'day_pass' && (field as any).name === 'desks'
            ? {
                helperText: simpleBlock(
                  !desks.isLoading
                    ? `Day ${desks.data === 1 ? 'Pass' : 'Passes'} available: ${desks.data ?? 0}`
                    : 'Checking available day passes'
                ),
                options: Array.from({ length: desks.data ?? 0 }, (_, i) => ({
                  label: `${i + 1} ${i === 0 ? 'day pass' : 'day passes'}`,
                  value: `${i + 1}`,
                })),
                errorText:
                  desks.data === 0
                    ? simpleBlock('There are no day passes available at the moment. Please select a different date.')
                    : (field as any).errorText,
                disabled: desks.isLoading || desks.data === 0,
              }
            : {}),
          ...(isMeetingRoom && (field as any).name === 'startTime'
            ? {
                ...field,
                times: filteredTimeSlots,
              }
            : {}),
          ...(props.spaceType !== 'day_pass' &&
          field.type === 'calendar' &&
          field.name === 'date' &&
          !availability.data?.isAvailable
            ? {
                errorText: simpleBlock(
                  `These dates are no longer available for this ${
                    props.spaceType === 'meeting_room' ? 'room' : 'space'
                  }. Please select “Inquire” and our team will assist you in finding a suitable space for your booking dates.`
                ),
              }
            : {}),
        }))}
        id={inquiryOnly ? 'pricing_inquiry' : undefined}
        state={{ ...state, startTime: { ...(state.startTime ?? {}), loading: fetching } }}
        primaryLabel={fetching ? 'Loading' : buttonState.primaryLabel}
        primaryDisabled={buttonState.primaryDisabled}
        secondaryDisabled={buttonState.primaryDisabled}
        onChange={onChange}
        onSubmit={onSubmit}
        isCheckCleared={isCheckCleared}
        setIsCheckCleared={setIsCheckCleared}
        notification={{
          ...message,
          dismiss: () => setIsNotification(false),
          visible: isNotification && !!message?.message,
          id: 'form_success',
        }}
        onSecondaryClick={onInquire}
        holidays={props.holidays}
      />

      {!inquiryOnly && (
        <OrSecondaryNavbarPdp
          building={props.description.location}
          workspaceType={props.description.type}
          space={props.description.title}
          date={state.date?.value as DateRange | undefined}
          price={(pricing.reservation as FeeProps).fee}
          rate={props.prices?.unitPrice ? priceLabel(props.prices?.unitPrice, props.spaceType) : ''}
          discountedPrice={(pricing.reservation as FeeProps).discountedFee}
          time={navBarTimeCheck()}
          onAction={() => {
            checkBookingState()
            const date = state.date?.value as DateRange | undefined
            const title = !date?.from
              ? 'Check availability'
              : !date?.to && !navBarTimeCheck()
              ? 'Select a time'
              : 'Book now'
            trackNavigationMenuClick({
              menuTitle: title?.toLocaleLowerCase() || '',
              menuHeading: title?.toLocaleLowerCase() || '',
              menuType: 'secondary navigation',
              linkUrl: '',
              componentName: 'secondary nav bar pdp',
              windowLocationHref: window.location.href,
            })
          }}
        />
      )}
    </>
  )
}
